import { useParams } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import axios from 'axios';
import { PublicPageCloseButton } from '../../components/PublicPageCloseButton';
import { usePublicVerify } from './hooks';

export function PublicVerifyPage() {
  const { signatureId = '' } = useParams();
  const { data, isLoading, error } = usePublicVerify(signatureId);

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4">
      <div className="max-w-lg mx-auto space-y-4">
        <header className="flex items-center justify-between">
          <div className="text-sky-700 font-bold text-lg">ReceiptRaptor</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-500">Signature verification</div>
            <PublicPageCloseButton />
          </div>
        </header>

        {isLoading && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
            Looking up signature…
          </div>
        )}

        {error && (
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
            <XCircle className="mx-auto text-red-600" size={28} />
            <h2 className="text-lg font-semibold text-slate-800 mt-2">Not found</h2>
            <p className="text-sm text-slate-600 mt-2">
              {axios.isAxiosError(error) && error.response?.status === 429
                ? 'Too many requests — please wait a minute.'
                : 'We could not find a matching document or signature.'}
            </p>
          </div>
        )}

        {data && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              {data.signed ? (
                <CheckCircle2 className="text-green-600" size={32} />
              ) : (
                <Clock className="text-amber-600" size={32} />
              )}
              <div>
                <h2 className="text-xl font-semibold text-slate-800">
                  {data.signed ? 'Signed' : 'Awaiting signature'}
                </h2>
                {data.tenantBusinessName && (
                  <p className="text-sm text-slate-600">Issued by {data.tenantBusinessName}</p>
                )}
              </div>
            </div>

            <dl className="text-sm grid grid-cols-[auto,1fr] gap-x-4 gap-y-2">
              {data.templateName && (
                <>
                  <dt className="text-slate-500">Document</dt>
                  <dd className="text-slate-800">{data.templateName} ({humanize(data.documentKind)})</dd>
                </>
              )}
              {data.signed && data.typedName && (
                <>
                  <dt className="text-slate-500">Signed by</dt>
                  <dd className="text-slate-800">{data.typedName}</dd>
                </>
              )}
              {data.signed && data.signedAtUtc && (
                <>
                  <dt className="text-slate-500">Signed at</dt>
                  <dd className="text-slate-800">{new Date(data.signedAtUtc).toLocaleString()}</dd>
                </>
              )}
              {data.documentSha256Prefix && (
                <>
                  <dt className="text-slate-500">Hash prefix</dt>
                  <dd className="text-slate-800 font-mono text-xs">{data.documentSha256Prefix}</dd>
                </>
              )}
              {data.effectiveUntilUtc && (
                <>
                  <dt className="text-slate-500">Effective</dt>
                  <dd className="text-slate-800">
                    {data.effectiveFromUtc
                      ? `${new Date(data.effectiveFromUtc).toLocaleDateString()} – ${new Date(data.effectiveUntilUtc).toLocaleDateString()}`
                      : `Until ${new Date(data.effectiveUntilUtc).toLocaleDateString()}`}
                  </dd>
                </>
              )}
              <dt className="text-slate-500">Currently effective</dt>
              <dd className={data.isCurrentlyEffective ? 'text-green-700' : 'text-red-700'}>
                {data.isCurrentlyEffective ? 'Yes' : 'No'}
              </dd>
            </dl>

            <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
              Level A signature per ReceiptRaptor policy. Not a qualified e-signature. Contact the
              issuing business if anything here is unexpected.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}

function humanize(kind: string | null): string {
  if (!kind) return '—';
  return {
    PowerOfAttorney: 'Power of attorney',
    VetAuthorization: 'Vet authorization',
    IncapacitationDirective: 'Incapacitation directive',
    Other: 'Other'
  }[kind] ?? kind;
}
